import { useNavigate } from "react-router-dom";

import { Button, Link, Loader, Notification, Select } from "@stellar/design-system";

import { DialogModal } from "@/components/DialogModal";

import { CO_SIGNING_MAX_SIGNERS, Routes } from "@/constants/settings";

import { getEnrollmentErrorMessage } from "@/helpers/enrollmentErrors";
import { formatDate } from "@/helpers/formatIntlDateTime";

import { useCoSignerWizard } from "../hooks/useCoSignerWizard";

import { CoSignerKeyStep } from "./CoSignerKeyStep";
import { CoSignerStepper } from "./CoSignerStepper";
import { ReviewDetails } from "./ReviewDetails";

import { AppError, MultisigEnrollmentSession } from "@/types";

type CoSignerWizardProps = {
  orgName: string;
  currentUserId: string;
  pendingInviteeId?: string;
  activeSession?: MultisigEnrollmentSession | null;
  onBack: () => void;
  onDone: () => void;
  onGoToTeamMembers: () => void;
  editMode?: boolean;
};

export const CoSignerWizard = ({
  orgName,
  currentUserId,
  pendingInviteeId = "",
  activeSession = null,
  onBack,
  onDone,
  onGoToTeamMembers,
  editMode = false,
}: CoSignerWizardProps) => {
  const navigate = useNavigate();

  const {
    stepIdx,
    goNext,
    goPrev,
    canNextFromChooseOwner,
    canNextFromKeyStep,

    usersQuery,
    eligibleOwners,
    secondOwnerId,
    setSecondOwnerId,
    secondOwner,
    inviteeName,

    keyResult,
    handleKeyConnected,
    handleKeyDisconnected,
    disconnectWalletRef,
    signRef,

    signRequestPending,
    signError,

    confirmed,
    handleConfirm,
    confirmedHeading,
    confirmedBody,

    inResendMode,
    isResendInvitation,
    executeResend,
    modalAction,
    setModalAction,
    executeCancelInvitation,
    cancelNotice,

    isInviteModalOpen,
    isConfirmModalOpen,
    cancelExit,
    handleConfirmExit,

    initiateMutation,
    submitKeyMutation,
    verifyKeyMutation,
    cancelMutation,

    owner1Identity,
  } = useCoSignerWizard({
    orgName,
    currentUserId,
    pendingInviteeId,
    activeSession,
    editMode,
    onBack,
  });

  const fullName = (u: { first_name?: string; last_name?: string; email: string }) =>
    `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.email;

  return (
    <>
      <CoSignerStepper currentIdx={stepIdx} />

      <div className="MultisigSetup__card">
        {cancelNotice && (
          <div className="Notification__warning">
            <Notification variant="warning" title="Invitation canceled" isFilled>
              {cancelNotice}
            </Notification>
          </div>
        )}
        {initiateMutation.isError && (
          <div className="Notification__error">
            <Notification variant="error" title="Couldn't start co-signing setup" isFilled>
              {getEnrollmentErrorMessage(initiateMutation.error)}
            </Notification>
          </div>
        )}
        {submitKeyMutation.isPending && stepIdx === 1 && (
          <div className="Notification__warning">
            <Notification variant="primary" title="Registering your co-signing key…" isFilled>
              Sending your public key to the server. This usually takes a moment.
            </Notification>
          </div>
        )}
        {submitKeyMutation.isError && stepIdx === 1 && (
          <div className="Notification__error">
            <Notification variant="error" title="Couldn't register your co-signing key" isFilled>
              {getEnrollmentErrorMessage(submitKeyMutation.error)} Disconnect the wallet and scan
              the code again to retry.
            </Notification>
          </div>
        )}
        {stepIdx === 1 && signRequestPending && (
          <div className="Notification__warning">
            <Notification
              variant="primary"
              title="Approve the signing request in VIA Wallet"
              isFilled
            >
              We sent a signing request to your paired wallet. Confirm it there to finish setting up
              your co-signing key.
            </Notification>
          </div>
        )}
        {stepIdx === 1 && signError && (
          <div className="Notification__error">
            <Notification variant="error" title="Couldn't sign in VIA Wallet" isFilled>
              {signError}
            </Notification>
          </div>
        )}
        {stepIdx === 1 && verifyKeyMutation.isError && (
          <div className="Notification__error">
            <Notification variant="error" title="Couldn't verify your co-signing key" isFilled>
              {getEnrollmentErrorMessage(verifyKeyMutation.error)}
            </Notification>
          </div>
        )}
        {cancelMutation.isError && (
          <div className="Notification__error">
            <Notification variant="error" title="Couldn't cancel the invitation" isFilled>
              {getEnrollmentErrorMessage(cancelMutation.error)}
            </Notification>
          </div>
        )}

        {stepIdx === 0 && (
          <>
            <h2 className="MultisigSetup__title" style={{ textAlign: "left" }}>
              {inResendMode ? "Edit co-signer setup" : "Choose Owner 2"}
            </h2>
            <p className="MultisigSetup__p">
              The person you choose becomes a co-signer for {orgName}. We&apos;ll notify them in VIA
              Disbursement Platform to complete their own setup. Co-signing turns on once they
              finish. <br />
              <span>
                Don&apos;t see the right person? Add or manage Owners in{" "}
                <Link
                  onClick={() => {
                    navigate(`${Routes.SETTINGS}?tab=tm`);
                  }}
                >
                  Team members.
                </Link>
              </span>
            </p>

            {usersQuery.isLoading && (
              <div className="MultisigConnect__pending">
                <Loader size="1rem" />
                <span>Loading Owners and Financial Controllers…</span>
              </div>
            )}
            {usersQuery.error && (
              <Notification
                variant="error"
                title="Could not load Owners and Financial Controllers"
                isFilled
              >
                {(usersQuery.error as AppError).message ?? "Try again."}
              </Notification>
            )}

            {!usersQuery.isLoading && !usersQuery.error && (
              <div>
                <div className="MultisigSetup__field">
                  <Select
                    fieldSize="md"
                    id="second-owner"
                    label="Owner 2"
                    value={secondOwnerId}
                    disabled={inResendMode || !!activeSession}
                    note={
                      inResendMode && activeSession?.created_at ? (
                        <p className="MultisigSetup__p">
                          Invitation sent {formatDate(activeSession.created_at)}
                        </p>
                      ) : null
                    }
                    onChange={(e) => setSecondOwnerId(e.target.value)}
                  >
                    <option value="">Select an Owner or Financial Controller</option>
                    {eligibleOwners.map((o) => (
                      <option key={o.id} value={o.id}>
                        {fullName(o)} — {o.email}
                      </option>
                    ))}
                  </Select>
                  {inResendMode && (
                    <div className="MultisigSetup__inlineActions">
                      <Button
                        variant="primary"
                        size="md"
                        onClick={() => executeResend()}
                        disabled={isResendInvitation || initiateMutation.isPending}
                        isLoading={initiateMutation.isPending && inResendMode}
                        icon={
                          isResendInvitation && (
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#ffffff"
                              strokeWidth="1.75"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              name="check"
                            >
                              <path d="M20 6 9 17l-5-5"></path>
                            </svg>
                          )
                        }
                        iconPosition="left"
                      >
                        {isResendInvitation ? "Invitation sent" : "Re-send invitation"}
                      </Button>
                      <Button variant="tertiary" size="md" onClick={() => setModalAction("cancel")}>
                        Cancel invitation
                      </Button>
                    </div>
                  )}
                </div>
                {eligibleOwners.length === 0 && (
                  <p className="MultisigSetup__p">
                    No other active Owners or Financial Controllers found. Add one from the Settings
                    page, then return here.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {stepIdx >= 1 && !confirmed && (
          <div style={{ display: stepIdx === 1 ? undefined : "none" }}>
            <CoSignerKeyStep
              userId={currentUserId || "sdp-multisig-owner"}
              onConnected={handleKeyConnected}
              onDisconnected={handleKeyDisconnected}
              disconnectRef={disconnectWalletRef}
              signRef={signRef}
            />
          </div>
        )}

        {stepIdx === 2 && !confirmed && (
          <>
            <h2 className="MultisigSetup__title" style={{ textAlign: "left" }}>
              Review
            </h2>
            <p className="MultisigSetup__p">
              Once the second Owner finishes setting up their co-signing key, co-signing turns on
              for your organization.
            </p>
            <hr className="MultisigSetup__rule" />
            <ReviewDetails
              owner1={{ name: owner1Identity.name, email: owner1Identity.email }}
              owner2={{
                name: secondOwner ? fullName(secondOwner) : undefined,
                email: secondOwner?.email,
              }}
              youOwner={1}
              publicKey={keyResult?.publicKey ?? ""}
            />
          </>
        )}

        {stepIdx === 2 && confirmed && (
          <>
            <h2 className="MultisigSetup__title MultisigSetup__title--ok">
              <span className="MultisigConnect__check MultisigConnect__check--lg" aria-hidden>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  name="check"
                >
                  <path d="M20 6 9 17l-5-5"></path>
                </svg>
              </span>
              {confirmedHeading}
            </h2>
            <p className="MultisigSetup__p">{confirmedBody}</p>
            <hr className="MultisigSetup__rule" />
            <ReviewDetails
              owner1={{ name: owner1Identity.name, email: owner1Identity.email }}
              owner2={{
                name: secondOwner ? fullName(secondOwner) : undefined,
                email: secondOwner?.email,
              }}
              youOwner={1}
              publicKey={keyResult?.publicKey ?? verifyKeyMutation.data?.owner1_public_key ?? ""}
            />
          </>
        )}
      </div>

      {confirmed && (
        <div className="MultisigSetup__card">
          <h2 className="MultisigSetup__title" style={{ textAlign: "left" }}>
            Add more co-signers
          </h2>
          <p className="MultisigSetup__p">
            You can add up to {CO_SIGNING_MAX_SIGNERS} co-signers once co-signing is on. Only team
            members with the Financial Controller or Owner role can be co-signers, so give them that
            role first in Team members.
          </p>
        </div>
      )}

      <div className={`MultisigSetup__footer${confirmed ? " MultisigSetup__footer--end" : ""}`}>
        {!confirmed && (
          <Button variant="tertiary" size="md" onClick={goPrev}>
            ‹ Back
          </Button>
        )}
        {confirmed ? (
          <div className="MultisigSetup__footerActions">
            <Button variant="tertiary" size="md" onClick={onGoToTeamMembers}>
              Go to team members
            </Button>
            <Button variant="primary" size="md" onClick={onDone}>
              Done
            </Button>
          </div>
        ) : stepIdx === 2 ? (
          <Button variant="primary" size="md" onClick={handleConfirm}>
            Confirm
          </Button>
        ) : (
          <Button
            variant="primary"
            size="md"
            onClick={goNext}
            disabled={
              (stepIdx === 0 && (!canNextFromChooseOwner || initiateMutation.isPending)) ||
              (stepIdx === 1 &&
                (!canNextFromKeyStep || signRequestPending || verifyKeyMutation.isPending))
            }
            icon={
              (stepIdx === 0 && initiateMutation.isPending) ||
              (stepIdx === 1 && (signRequestPending || verifyKeyMutation.isPending)) ? (
                <Loader size="0.85rem" />
              ) : undefined
            }
            iconPosition="left"
          >
            {stepIdx === 0 && initiateMutation.isPending
              ? "Sending invitation…"
              : stepIdx === 1 && signRequestPending
                ? "Waiting for VIA Wallet…"
                : stepIdx === 1 && verifyKeyMutation.isPending
                  ? "Verifying key…"
                  : "Next"}
          </Button>
        )}
      </div>

      {/* 1. Dialog cancel invitation */}
      <DialogModal
        visible={modalAction !== "none"}
        width="50"
        onClose={() => setModalAction("none")}
        title={`Cancel invitation to ${inviteeName}?`}
        footerAlignment="stretch"
        primaryAction={{
          label: "Keep invitation",
          variant: "tertiary",
          onClick: () => setModalAction("none"),
        }}
        secondaryAction={{
          label: `Cancel invitation`,
          variant: "destructive",
          onClick: executeCancelInvitation,
          disabled: cancelMutation.isPending,
          isLoading: cancelMutation.isPending,
        }}
      >
        <p>They won't be able to finish co-signer setup. You can invite them again later.</p>
      </DialogModal>

      {/* 1. Exit without inviting */}
      <DialogModal
        visible={isInviteModalOpen}
        width="50"
        onClose={cancelExit}
        title={`Exit without inviting ${inviteeName}?`}
        footerAlignment="stretch"
        primaryAction={{
          label: "Finish setup",
          variant: "tertiary",
          onClick: cancelExit,
        }}
        secondaryAction={{
          label: "Exit setup",
          variant: "destructive",
          onClick: handleConfirmExit,
        }}
      >
        <p>
          You've chosen {inviteeName}, but they won't be invited until you confirm setup. If you
          exit now, no invitation is sent.
        </p>
      </DialogModal>

      {/* 2. Exit before confirming */}
      <DialogModal
        visible={isConfirmModalOpen}
        width="50"
        onClose={cancelExit}
        title="Exit before confirming?"
        footerAlignment="stretch"
        primaryAction={{
          label: "Keep setting up",
          variant: "tertiary",
          onClick: cancelExit,
        }}
        secondaryAction={{
          label: "Exit without confirming",
          variant: "destructive",
          onClick: handleConfirmExit,
        }}
      >
        <p>
          You've set up your co-signing key, but co-signing won't turn on until you confirm. You can
          come back to finish anytime.
        </p>
      </DialogModal>
    </>
  );
};
